<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-1 text-center" v-text="`${isEditMode ? 'Edycja' : 'Wybór'} sklepów objętych umową`" />
            <h5 class="mb-0 text-center" v-text="operator.name" />
        </template>

        <h4 v-text="'Sklepy'" />
        <p
            v-text="`Zaznacz sklepy objęte umową i uzupełnij numer punktu
            zbiórki dla każdego z nich aby dokonać integracji z Operatorem`"
            class="text-primary"
        />

        <mass-selection-buttons
            class="my-4"
            :value="locations.every(el => el.selected)"
            @select="selectAllLocations($event)"
        />

        <div class="locations-list">
            <accordion
                v-for="location in locations"
                class="drs-shop"
                header-class="drs-shop__header"
                :key="location.locationId"
                :class="{ selected: location.selected }"
                :is-active="expandedShopId === location.locationId"
            >
                <template #header>
                    <ui-checkbox-input
                        class="text-sm"
                        :name="`shop-${location.locationId}`"
                        :label="`${location.full_name}, ${location.fullAddress}`"
                        :value="location.selected"
                        @change="toggleShop(location.locationId)"
                    />
                </template>
                <template #expand-button>
                    <div class="text-md cursor-pointer nowrap" @click="toggleShopExpand(location.locationId)">
                        <strong v-text="'Konfiguruj'" />
                        <a-icon class="pl-1" :icon="expandedShopId === location.locationId ? 'ArrowUp' : 'ArrowDown'" />
                    </div>
                </template>

                <template #body>
                    <div class="d-flex flex-wrap gap-1">
                        <the-input
                            required
                            class="cux-exclude mb-0 text-primary"
                            label="Numer punktu zbiórki"
                            :style="{ width: '300px', maxWidth: '100%' }"
                            :tooltip="cpIdTooltip"
                            :text="location.collectionPointId"
                            :mask="cpIdMask"
                            :placeholder="cpIdPlaceholder"
                            :maxlength="cpIdMaxLength"
                            :invalid="location.invalidCollectionPointId"
                            :invalid-feedback="cpIdFeedback"
                            @input="onCpIdInput(location.locationId, $event)"
                            @blur="onCpIdBlur(location.locationId, $event)"
                        />
                        <the-input
                            v-if="isRetailerTokenRequired"
                            required
                            class="text-primary"
                            label="Token operatora"
                            :style="{ width: '300px', maxWidth: '100%' }"
                            :tooltip="retailerTokenTooltip"
                            :text="location.retailerToken"
                            @input="onRetailerTokenInput(location.locationId, $event)"
                        />
                    </div>
                </template>
            </accordion>
        </div>

        <template #footer>
            <the-btn
                text="Zamknij"
                class="btn-outline-custom"
                @click="$emit('close-modal')"
            />
            <the-btn
                text="Zapisz"
                class="btn-secondary"
                :icon="isLoading ? 'Spinner' : ''"
                :loading-status="isLoading ? 'loading' : 'idle'"
                :disabled="!canSave || isLoading"
                @click="save"
            />
        </template>
    </ui-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { CONTRACT_LOCATION_STATUSES } from '@/utils/constants/drs/operators'
import cloneDumb from '@/helpers/cloneDumb'
import { CONTRACT_MODES, SUMMARY_STATUSES } from '@/components/drs/operators/-utils'
import operatorExternalDataMixin from '@/components/drs/operators/operator-external-data.mixin'

export default {
    mixins: [operatorExternalDataMixin],

    data() {
        return {
            locations: [],
            expandedShopId: null,
            isLoading: false
        }
    },

    computed: {
        ...mapGetters('drs', ['shopsWithActiveService']),
        ...mapGetters('drsOperators', ['operatorContract']),

        isEditMode() {
            return this.modalArgs.mode === CONTRACT_MODES.EDIT
        },

        modalArgs() {
            return this.$store.getters.modalState.args
        },

        operator() {
            return this.modalArgs.operator
        },

        operatorCode() {
            return this.operator?.code
        },

        contract() {
            return this.modalArgs.contract
        },

        canSave() {
            const selectedLocations = this.locations.filter(location => location.selected)

            if (!this.isEditMode) {
                return selectedLocations.length > 0 &&
                    selectedLocations.every(location => this.isLocationValid(location))
            }

            // Check if any selected location has changed its collectionPointId or retailerToken
            const hasChanges = selectedLocations.some(loc => {
                const originalLocation = this.operatorContract.subscriptions.find(s => s.locationId === loc.locationId)
                if (!originalLocation) return true // New selection
                const originalCollectionPointId = originalLocation.externalData?.collection_point_id || ''
                const originalRetailerToken = originalLocation.externalData?.retailer_token || ''
                return loc.collectionPointId !== originalCollectionPointId ||
                    loc.retailerToken !== originalRetailerToken
            })

            // Check if selected locations are the same as in the original contract
            const originalSelectedLocationIds = this.operatorContract.subscriptions
                .filter(s => s.fractions.length)
                .map(s => s.locationId)
            const selectedLocationIds = selectedLocations.map(loc => loc.locationId)
            const isSameSelection = selectedLocationIds.length === originalSelectedLocationIds.length &&
                selectedLocationIds.every(id => originalSelectedLocationIds.includes(id))

            return selectedLocations.every(location => this.isLocationValid(location)) &&
                    (hasChanges || !isSameSelection || this.modalArgs.hasChanges)
        }
    },

    methods: {
        ...mapActions('drsOperators', ['createContractDetail', 'saveExternalData', 'deleteContractDetail', 'loadAndMapContract']),

        isLocationValid(location) {
            const hasCollectionPointId = Boolean((location.collectionPointId || '').trim())
            const hasRetailerToken = !this.isRetailerTokenRequired || Boolean((location.retailerToken || '').trim())

            return hasCollectionPointId &&
                this.isValidCpId(location.collectionPointId) &&
                hasRetailerToken
        },

        async save() {
            if (!this.canSave || this.isLoading) {
                return
            }

            this.isLoading = true

            const locations = cloneDumb(this.locations)

            const contractFractions = this.operatorContract.fractions
            const contractHandlingFee = this.operatorContract.defaultHandlingFee
            const contractHandlingFeeRvm = this.operatorContract.defaultHandlingFeeRvm || undefined
            const operatorServiceId = this.operatorContract.operatorServiceId

            const locationsInProcess = []
            const pushLocationInProcess = (location) => {
                if (!locationsInProcess.some(loc => loc.locationId === location.locationId)) {
                    locationsInProcess.push(location)
                }
            }

            // this.createContractDetail for every selected location
            for (const location of locations) {
                // If location changed selected state or collectionPointId
                // or retailerToken comparing to the existing contract
                const cLocation = this.operatorContract.subscriptions.find(s => s.locationId === location.locationId)
                const cLocationStatus = cLocation.locStatus === CONTRACT_LOCATION_STATUSES.ACTIVE

                const isSelectionChanged = cLocationStatus !== location.selected
                const isExternalDataChanged = Boolean(
                    (
                        location.collectionPointId &&
                        cLocation.externalData?.collection_point_id !== location.collectionPointId
                    ) ||
                    (
                        location.retailerToken &&
                        cLocation.externalData?.retailer_token !== location.retailerToken
                    )
                )
                const hasChanges = isExternalDataChanged || isSelectionChanged

                let contractIdsToRemove = []
                if (hasChanges) {
                    contractIdsToRemove = cLocation.fractions.map(fr => fr.contractId)
                    pushLocationInProcess(location)

                    // Remove fractions, so that they are not created again in the next step
                    location.fractions = []
                }

                // Statuses shown in the edit summary
                location.assignmentStatus = isSelectionChanged ? SUMMARY_STATUSES.SUCCESS : SUMMARY_STATUSES.UNCHANGED
                location.externalDataStatus = SUMMARY_STATUSES.UNCHANGED

                for (const fr of location.fractions) {
                    // Check if fraction id exists in the operatorContract fractions
                    const fractionInContract = contractFractions.find(f => f === fr.id)
                    if (!fractionInContract) {
                        contractIdsToRemove.push(fr.contractId)
                        pushLocationInProcess(location)
                        location.assignmentStatus = SUMMARY_STATUSES.SUCCESS
                        continue
                    }
                    if (fr.handlingFee !== contractHandlingFee) {
                        contractIdsToRemove.push(fr.contractId)
                        pushLocationInProcess(location)
                        location.assignmentStatus = SUMMARY_STATUSES.SUCCESS
                        continue
                    }
                    if (fr.handlingFeeRvm !== contractHandlingFeeRvm) {
                        contractIdsToRemove.push(fr.contractId)
                        pushLocationInProcess(location)
                        location.assignmentStatus = SUMMARY_STATUSES.SUCCESS
                        continue
                    }
                }

                await Promise.all(contractIdsToRemove.map(id => this.deleteContractDetail(id)))
                    .catch(err => {
                        location.assignmentStatus = SUMMARY_STATUSES.ERROR
                        console.error('Error deleting contract details:', err)
                    })

                if (!location.selected) {
                    // No changes, skip creating contract detail
                    continue
                }
                const payload = {
                    locationId: location.locationId,
                    handlingFee: contractHandlingFee,
                    handlingFeeRvm: contractHandlingFeeRvm ?? undefined,
                    operatorContract: `/api/operator_contracts/${this.operatorContract.id}`
                }

                const [first, ...rest] = contractFractions
                try {
                    await this.createContractDetail({
                        ...payload,
                        fractionId: first
                    })
                } catch(err) {
                    location.assignmentStatus = SUMMARY_STATUSES.ERROR
                    if (location.selected && cLocationStatus !== location.selected) {
                        location.selected = false // To show error on next step
                    }
                }

                await Promise.all(rest.map(async (fraction) => {
                    try {
                        await this.createContractDetail({
                            ...payload,
                            fractionId: fraction
                        })
                    } catch {
                        location.assignmentStatus = SUMMARY_STATUSES.ERROR
                        if (location.selected && cLocationStatus !== location.selected) {
                            location.selected = false // To show error on next step
                        }
                    }
                }))

                // Save external data
                try {
                    await this.saveExternalData({
                        locationId: location.locationId,
                        operatorServiceId,
                        collectionPointId: location.collectionPointId,
                        retailerToken: location.retailerToken
                    })
                    if (isExternalDataChanged) {
                        location.externalDataStatus = SUMMARY_STATUSES.SUCCESS
                    }
                } catch {
                    if (isExternalDataChanged) {
                        location.externalDataStatus = SUMMARY_STATUSES.ERROR
                    }
                    location.collectionPointId = ''
                    this.$store.dispatch(
                        'addNotification',
                        {
                            type: 'danger',
                            content: 'Przy zapisie numeru punktu zbiórki wystąpił błąd'
                        },
                        { root: true }
                    )
                }
            }
            await this.loadAndMapContract(this.operatorContract.operatorServiceId)

            this.isLoading = false

            this.$store.dispatch('openModal', {
                componentPath: 'drs/operators/ContractLocationsSummary',
                args: {
                    locations: locationsInProcess,
                    operator: this.operator,
                    contract: this.contract,
                    mode: this.modalArgs.mode
                }
            })
        },

        selectAllLocations(value) {
            this.locations = this.locations.map(location => ({
                ...location,
                selected: value
            }))
        },

        updateLocation(locationId, updatedFields) {
            this.locations = this.locations.map(location => {
                if (location.locationId === locationId) {
                    return { ...location, ...updatedFields }
                }
                return location
            })
        },

        toggleShop(locationId) {
            this.updateLocation(locationId, {
                selected: !this.locations.find(loc => loc.locationId === locationId)?.selected
            })
        },

        toggleShopExpand(locationId) {
            this.expandedShopId = this.expandedShopId === locationId ? null : locationId
        },

        onCpIdInput(locationId, value) {
            this.updateLocation(locationId, {
                collectionPointId: value,
                invalidCollectionPointId: false
            })
        },

        onCpIdBlur(locationId, value) {
            const val = typeof value === 'string' ? value : value?.target?.value || ''
            this.updateLocation(locationId, {
                collectionPointId: val,
                invalidCollectionPointId: !this.isValidCpId(val)
            })
        },

        onRetailerTokenInput(locationId, value) {
            this.updateLocation(locationId, {
                retailerToken: value
            })
        }
    },

    created() {
        this.locations = this.operatorContract.subscriptions.map(loc => {
            return {
                ...loc,
                selected: loc.locStatus === CONTRACT_LOCATION_STATUSES.ACTIVE,
                collectionPointId: loc.externalData?.collection_point_id || '',
                retailerToken: loc.externalData?.retailer_token || ''
            }
        })
    }
}
</script>